import * as d3 from 'd3';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as uuid from 'uuid';

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { SnAppliance, SnCamera } from '@lib/api/entities';
import { SnCamerasAPI } from '@lib/api/services';
import { SnPlayerState } from '@lib/player/common';
import { BASE_COLORS } from '@lib/ui/theme';

import { CameraAddRuleDialogComponent } from '../camera-add-rule-dialog/camera-add-rule-dialog.component';

export class Shape {
  public id: string;
  public points: any;
  public gridPoints: any[];
  public ruleName: string;
  constructor() {
    this.points = [];
    this.gridPoints = [];
  }
}

@Component({
  selector: 'sn-camera-rules-dialog',
  templateUrl: './camera-rules-dialog.component.html',
  styleUrls: ['./camera-rules-dialog.component.scss'],
})
export class CameraRulesDialogComponent implements AfterViewInit, OnDestroy, OnInit {
  public svgWidth: number = 1000;
  public svgHeight: number = 600;
  public resolutionX: number;
  public resolutionY: number;

  public gridX: number;
  public gridY: number;
  public rules = null;
  public directions = null;
  public drawing: Shape;

  public zoneDrawn: boolean = false;
  public isPolygonComplete: boolean = false;
  public isShapeVisible: string[] = [];
  public wrongDirectionRules: number = 0;

  public canDelete: boolean = false;
  public isLegendOpen: boolean = false;
  private _svg;
  private _svgElement;
  private _startPoint: any;
  private _shapes = [];
  private _dragging: Shape;

  private readonly _playerState$ = new Subject<SnPlayerState>();
  private readonly _destroy$ = new Subject<void>();
  private _currentPlayerState: SnPlayerState = SnPlayerState.Pending;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      camera: SnCamera;
      appliance: SnAppliance;
    },
    private readonly _matDialog: MatDialog,
    private readonly _matDialogRef: MatDialogRef<CameraRulesDialogComponent>,
    private readonly _elementRef: ElementRef,
    private readonly _cd: ChangeDetectorRef,
    private readonly _cameraAPI: SnCamerasAPI,
  ) {}

  public ngOnInit(): void {}

  public ngAfterViewInit(): void {
    this._svg = d3.select('#aiGridCanvas');
    this._svgElement = this._elementRef.nativeElement.querySelector('.coveringCanvas');
    this._playerState$.pipe(takeUntil(this._destroy$)).subscribe((state) => {
      this._currentPlayerState = state;
      if (this.rules) {
        return;
      }
      if (state === SnPlayerState.Playing) {
        setTimeout(() => {
          this._genGridResolution();
          this.updateGridResolution();
          this._checkForAreaPoints();
          this._checkForDirections();
        }, 750);
      }
    });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  @HostListener('window:resize', ['$event'])
  public onResize(): void {
    if (this._svg && this._currentPlayerState === SnPlayerState.Playing) {
      this.updateGridResolution();
    }
  }

  public capitalizeLabel(label: string): string {
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  public onStateChange(state: SnPlayerState): void {
    this._playerState$.next(state);
  }

  public addNewRule(): void {
    const dialogRef = this._matDialog.open(CameraAddRuleDialogComponent, {
      data: {
        camera: this.data.camera,
        wrongDirectionRules: this.wrongDirectionRules,
        appliance: this.data.appliance,
      },
      width: '40vw',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((res) => {
        if (res) {
          this.data.camera.aiDetectionTypes[0] = res.aiDetectionTypes[0];
          const polygon = this._svg.selectAll('g');
          polygon.remove();
          this.isShapeVisible = [];
          this._shapes = [];
          this._checkForAreaPoints();
          this._checkForDirections();
          this._cd.detectChanges();
        }
      });
  }

  public updateGridResolution(): void {
    const svgBox = this._svgElement.getBBox();
    this.resolutionX = svgBox.width;
    this.resolutionY = svgBox.height;
    this.gridX = (svgBox.width - 1) / 160;
    this.gridY = (svgBox.height - 1) / 90;
    this.canDelete = true;
    this._cd.detectChanges();
  }

  public handleMouseUp(event: any): void {
    if (this._dragging || this.zoneDrawn) {
      return;
    }
    if (event.target.hasAttribute('is-handle')) {
      this._closePolygon(this.drawing);
      return;
    }
    const x = event.offsetX;
    const y = event.offsetY;
    const gridX = this._round(Math.max(5, Math.min(this.resolutionX - 5, x)), this.gridX);
    const gridY = this._round(Math.max(5, Math.min(this.resolutionY - 5, y)), this.gridY);
    this._startPoint = [gridX, gridY];

    this._updateShape(this.drawing);
  }

  public handleMouseMove(event: any): void {
    if (!this.drawing) {
      return;
    }
    if (event.target.id === 'gridRect' || event.target.hasAttribute('is-handle')) {
      const g = this._svg.select('#' + this.drawing.id);
      g.select('line').remove();
      g.insert('line', ':first-child')
        .attr('x1', this._startPoint[0])
        .attr('y1', this._startPoint[1])
        .attr('x2', event.offsetX)
        .attr('y2', event.offsetY)
        .attr('stroke', BASE_COLORS['accent-400'])
        .attr('stroke-width', 2);

      this._updateShape(this.drawing);
    }
  }

  public toggleShape(rule: any): void {
    if (this.isShapeVisible.includes(rule.name)) {
      const shapeIndex = this.isShapeVisible.findIndex((shapeVisibleName) => {
        return shapeVisibleName === rule.name;
      });

      const index = this._shapes.findIndex((areaShape) => {
        return areaShape.ruleName === rule.name;
      });

      const shape = this._shapes[index];
      const g = this._svg.select(`#${shape?.id}`);
      g.attr('visibility', 'hidden');

      const lineIndex = this._shapes.findIndex((lineDirection) => {
        return lineDirection.ruleName === `${rule.name}-directions`;
      });

      if (lineIndex >= 0) {
        const lineShape = this._shapes[lineIndex];
        const line = this._svg.select(`#${lineShape?.id}`);
        line.attr('visibility', 'hidden');
      }

      this.isShapeVisible.splice(shapeIndex, 1);
      return;
    }

    if (!this.isShapeVisible.includes(rule.name)) {
      const index = this._shapes.findIndex((areaShape) => {
        return areaShape.ruleName === rule.name;
      });

      const shape = this._shapes[index];
      const g = this._svg.select(`#${shape?.id}`);
      g.attr('visibility', 'visible');

      const lineIndex = this._shapes.findIndex((lineDirection) => {
        return lineDirection.ruleName === `${rule.name}-directions`;
      });

      if (lineIndex >= 0) {
        const lineShape = this._shapes[lineIndex];
        const line = this._svg.select(`#${lineShape?.id}`);
        line.attr('visibility', 'visible');
      }
      this.isShapeVisible.push(rule.name);
      return;
    }
  }

  public editRule(rule: any): void {
    const dialogRef = this._matDialog.open(CameraAddRuleDialogComponent, {
      data: {
        camera: this.data.camera,
        wrongDirectionRules: this.wrongDirectionRules,
        existingDirection: rule.type.direction,
        appliance: this.data.appliance,
        rule,
      },
      width: '40vw',
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((res) => {
        if (res) {
          this._shapes = [];
          this.isShapeVisible = [];
          const polygon = this._svg.selectAll('g');
          polygon.remove();
          this._checkForAreaPoints();
          this._checkForDirections();
        } else {
          return;
        }
        this._cd.detectChanges();
      });
  }

  public deleteRule(rule: any): void {
    const shapeIndex = this._shapes.findIndex((shape) => {
      return shape.ruleName === rule.name;
    });

    const ruleIndex = this.data.camera.aiDetectionTypes[0].meta.rules.findIndex((metaRule) => {
      return metaRule.name === rule.name;
    });

    this.data.camera.aiDetectionTypes[0].meta.rules.splice(ruleIndex, 1);

    const polygon = this._svg.selectAll('g');
    polygon.remove();

    this._checkForAreaPoints();

    this._checkForDirections();

    this._shapes.splice(shapeIndex, 1);
    this._cd.detectChanges();
  }

  public setDirection(rule: any): void {
    this.wrongDirectionRules += 1;
    const shape = new Shape();
    shape.id = `shape_${uuid.v4()}`;
    shape.ruleName = `${rule.name}-directions`;
    this._shapes.push(shape);
    this.drawing = shape;
    this._svg.append('g').attr('id', shape?.id);

    const g = this._svg.select('#' + this.drawing.id);

    g.append('circle')
      .attr('cx', 100)
      .attr('cy', 200)
      .attr('fill', 'white')
      .attr('fill-opacity', '0.9')
      .attr('r', 5)
      .attr('stroke', 'green')
      .attr('stroke-width', '2');
    g.append('circle')
      .attr('cx', 100)
      .attr('cy', 200)
      .attr('fill', 'white')
      .attr('fill-opacity', '0.9')
      .attr('r', 5)
      .attr('stroke', 'red')
      .attr('stroke-width', '2');

    this.drawing.points = { to: [100, 100], from: [100, 200] };

    this._closeDirection(this.drawing, rule);
  }

  public toggleLegend(): void {
    this.isLegendOpen = !this.isLegendOpen;
  }

  public updateCameraSettings(): void {
    if (this.data.camera.aiDetectionTypes[0].meta?.rules) {
      this._findAndUpdatePoints();
    }
    this._cameraAPI.updateCamera(this.data.camera.id, this.data.camera).subscribe(() => {
      this._matDialogRef.close(this.data.camera);
    });
  }

  private _findAndUpdatePoints(): void {
    this.rules.forEach((rule) => {
      const updatedPoints = [];
      let updatedDirectionPoints: any;
      const index = this._shapes.findIndex((shape) => {
        return shape.ruleName === rule.name;
      });
      rule.roi = this._shapes[index]?.points;

      rule.roi?.forEach((points) => {
        updatedPoints.push([
          Math.round(points[0] / this.gridX),
          Math.round(points[1] / this.gridY),
        ]);
      });

      rule.roi = [updatedPoints];

      const lineIndex = this._shapes.findIndex((shape) => {
        return shape.ruleName === `${rule.name}-directions`;
      });

      if (lineIndex >= 0) {
        rule.direction = this._shapes[lineIndex].points;

        const direction = rule.direction;

        const from = direction['from'] ? direction['from'] : direction[0];
        const to = direction['to'] ? direction['to'] : direction[1];

        updatedDirectionPoints = {
          to: [Math.round(to[0] / this.gridX), Math.round(to[1] / this.gridY)],
          from: [Math.round(from[0] / this.gridX), Math.round(from[1] / this.gridY)],
        };

        rule.direction = updatedDirectionPoints;
      }
    });

    this.data.camera.aiDetectionTypes[0].meta.rules = this.rules;
  }

  private _checkForAreaPoints(): void {
    this.rules = this.data.camera.aiDetectionTypes[0]?.meta?.rules;
    this.rules?.forEach((rule) => {
      const shape = new Shape();
      const gridPoints = [];
      this._startPoint = rule.roi[0];
      rule.roi.forEach((points) => {
        points.forEach((point) => {
          gridPoints.push([point[0] * this.gridX, point[1] * this.gridY]);
        });
      });
      this._startPoint = gridPoints[0];
      shape.id = `shape_${uuid.v4()}`;
      shape.points = gridPoints;
      shape.ruleName = rule.name;
      this._shapes.push(shape);
      this._svg.append('g').attr('id', shape?.id);
      this.drawing = shape;
      const g = this._svg.select('#' + this.drawing.id);
      this.drawing.points.push(this._startPoint);
      g.select('polyline').remove();
      g.append('polyline')
        .attr('points', this.drawing.points)
        .style('fill', 'none')
        .attr('stroke', `${rule.color}`)
        .attr('stroke-width', 2);

      this._updateShape(this.drawing, undefined, rule);
      this._closePolygon(this.drawing, rule);
    });
  }

  private _checkForDirections(): void {
    this.wrongDirectionRules = 0;
    const rules = this.data.camera.aiDetectionTypes[0]?.meta?.rules;
    rules?.forEach((rule) => {
      if (!rule.direction && rule.type === 'wrong-direction') {
        this.setDirection(rule);
      }

      if (rule.direction) {
        this.wrongDirectionRules += 1;
        const shape = new Shape();
        const gridPoints = [];
        gridPoints.push({
          to: [rule?.direction?.to[0] * this.gridX, rule?.direction?.to[1] * this.gridY],
          from: [rule?.direction?.from[0] * this.gridX, rule?.direction?.from[1] * this.gridY],
        });
        shape.id = `shape_${uuid.v4()}`;
        shape.points = gridPoints[0];
        shape.ruleName = `${rule.name}-directions`;
        this._shapes.push(shape);
        this._svg.append('g').attr('id', shape?.id);
        this.drawing = shape;
        const g = this._svg.select('#' + this.drawing.id);
        g.select('polyline').remove();
        g.append('polyline')
          .attr('points', [this.drawing.points['to'], this.drawing.points['from']])
          .style('fill', 'red')
          .attr('stroke', 'blue')
          .attr('stroke-width', 2);

        this._closeDirection(this.drawing, rule);
      }
    });
  }

  private _handleDrag(self: any): any {
    return function (d: any): void {
      const dragCircle = d3.select(this);
      const newPoints = [];
      let circle: d3.Selection<any, unknown, null, undefined>;
      const x = d3.event.x;
      const y = d3.event.y;
      const gridX = self._round(Math.max(5, Math.min(self.resolutionX - 5, x)), self.gridX);
      const gridY = self._round(Math.max(5, Math.min(self.resolutionY - 5, y)), self.gridY);

      const poly: any = d3.select(this.parentNode).select('polygon');
      const circles: any = d3.select(this.parentNode).selectAll('circle');
      dragCircle.attr('cx', gridX).attr('cy', gridY);
      for (let i = 0; i < circles._groups[0].length; i++) {
        circle = d3.select(circles._groups[0][i]);
        newPoints.push([Number(circle.attr('cx')), Number(circle.attr('cy'))]);
      }
      poly.attr('points', newPoints);

      self._dragging = self._shapes.find((s: { id: any }) => s.id === this.parentNode.id);
      self._dragging.points = newPoints;
    };
  }

  private _endDrag(self: any): any {
    return (d: any) => {
      self._dragging = null;
      self._findAndUpdatePoints();
    };
  }

  private _updateDirectionLine(shape: Shape, closed: boolean = false, rule?: any): void {
    this._updateDirectionPoints(shape, closed, rule);
  }

  // From is Green
  // To is Red
  private _updateDirectionPoints(shape: Shape, closed: boolean = false, rule?: any): void {
    const g = this._svg.select('#' + shape?.id);
    g.selectAll('circle').remove();

    const startPoint = shape.points.from;
    const startCircle = g
      .append('circle')
      .attr('cx', startPoint[0])
      .attr('cy', startPoint[1])
      .attr('fill', 'white')
      .attr('fill-opacity', '0.9')
      .attr('r', 6)
      .attr('stroke', 'green')
      .attr('stroke-width', '2');

    const endPoint = shape.points.to;
    const endCircle = g
      .append('circle')
      .attr('cx', endPoint[0])
      .attr('cy', endPoint[1])
      .attr('fill', 'white')
      .attr('fill-opacity', '0.9')
      .attr('r', 6)
      .attr('stroke', 'red')
      .attr('stroke-width', '2');

    if (closed) {
      const dragger = d3.drag().on('drag', this._handleDrag(this)).on('end', this._endDrag(this));
      startCircle.call(dragger).style('cursor', 'move');
      endCircle.call(dragger).style('cursor', 'move');
    }
  }

  private _updateShape(shape: Shape, closed: boolean = false, rule?: any): void {
    this._updatePoints(shape, closed, rule);
  }

  private _updatePoints(shape: Shape, closed: boolean = false, rule?: any): void {
    const g = this._svg.select('#' + shape?.id);
    g.selectAll('circle').remove();
    let pointLength = shape?.points.length - 1;
    if (closed) {
      pointLength++;
    }
    for (let i = 0; i < pointLength; i++) {
      const point = shape.points[i];
      const circle = g
        .append('circle')
        .attr('cx', point[0])
        .attr('cy', point[1])
        .attr('r', 6)
        .attr('fill', `${rule?.color || 'white'}`)
        .attr('fill-opacity', '0.9')
        .attr('stroke', '#000')
        .attr('stroke-width', '2');
      if (closed) {
        const dragger = d3.drag().on('drag', this._handleDrag(this)).on('end', this._endDrag(this));
        circle.call(dragger).style('cursor', 'move');
      }
    }
  }

  private _round(p: number, n: number): any {
    return p % n < n / 2 ? p - (p % n) : p + n - (p % n);
  }

  private _closeDirection(shape: Shape, rule?: any): void {
    const g = this._svg.select('#' + shape?.id);
    g.insert('polygon', ':first-child')
      .attr('points', [shape.points['from'], shape.points['to']])
      .attr('stroke', `${rule.color}`)
      .style('stroke-dasharray', '4, 16')
      .attr('stroke-width', 4);

    g.select('polyline').remove();
    g.select('line').remove();
    this.drawing.points = { from: shape.points['from'], to: shape.points['to'] };
    this._updateDirectionLine(shape, true, rule);
    this.drawing = null;
    this.isPolygonComplete = true;
    this.isShapeVisible.push(`${rule.name}-direction`);
  }

  private _closePolygon(shape: Shape, rule?: any): void {
    const g = this._svg.select('#' + shape?.id);
    g.insert('polygon', ':first-child')
      .attr('points', shape.points)
      .style('fill-opacity', '0.1')
      .style('fill', `${rule.color}`)
      .attr('stroke', `${rule.color}`)
      .attr('stroke-width', 2);

    g.select('polyline').remove();
    g.select('line').remove();
    this.drawing.points.splice(0, 1);
    this._updateShape(shape, true, rule);
    this.drawing = null;
    this.isPolygonComplete = true;
    this.isShapeVisible.push(rule.name);
  }

  private _genGridResolution(): void {
    const element = this._elementRef.nativeElement;
    const video: HTMLVideoElement[] = element.querySelectorAll('video');
    const topVideo = video[video.length - 1];
    const videoRatio = topVideo.videoHeight / topVideo.videoWidth;
    const clientRatio = topVideo.clientHeight / topVideo.clientWidth;
    if (videoRatio > clientRatio) {
      const ratio = topVideo.clientHeight / topVideo.videoHeight;
      const newVideoWidth = topVideo.videoWidth * ratio;
      this.svgWidth = newVideoWidth;
      this.svgHeight = topVideo.clientHeight;
      this._svgElement.style.left = `calc(50% - ${newVideoWidth / 2}px)`;
      this._svgElement.style.top = '0px';
      this._cd.detectChanges();
    } else {
      const ratio = topVideo.clientWidth / topVideo.videoWidth;
      const newVideoHeight = topVideo.videoHeight * ratio;
      if (!isNaN(newVideoHeight)) {
        this.svgHeight = newVideoHeight;
        this.svgWidth = topVideo.clientWidth;
        this._svgElement.style.top = `calc(50% - ${newVideoHeight / 2}px)`;
        this._svgElement.style.left = '0px';
        this._cd.detectChanges();
      }
    }
  }
}
